/**
 * ContractModal — Crear/editar contrato de trabajo + configuración previsional.
 */
import { afpOptions } from '../constants/afp'
import { useEffect } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { useForm, Controller } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import DatePicker from '@/components/DatePicker'
import { inputCls, labelCls, errorCls } from '@/lib/formStyles'
import type { Employee } from '../hooks/usePayroll'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

interface Props {
  open: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  isPending: boolean
  initial?: any | null
  employees: Partial<Employee>[]
}

const AFP_OPTIONS = afpOptions()

export default function ContractModal({ open, onClose, onSubmit, isPending, initial, employees }: Props) {
  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm()

  useEffect(() => {
    if (open) reset(initial || {
      contract_type: 'indefinido',
      work_hours_weekly: '45',
      gratification_mode: 'legal',
      is_current: true,
    })
  }, [open, initial, reset])

  const contractType = watch('contract_type')
  const gratificationMode = watch('gratification_mode')
  const isEdit = !!initial?.id

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [open, onClose])

  if (!open) return null

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar contrato' : 'Nuevo contrato'} size="2xl">
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 min-h-0 overflow-y-auto space-y-3">
        {/* Empleado */}
        {!isEdit && (
          <div>
            <label htmlFor="contractmodal-employee" className={labelCls}>Empleado <span className="text-danger-solid">*</span></label>
            <Controller name="employee" control={control} rules={{ required: 'Seleccione un empleado.' }}
              render={({ field }) => (
                <EntityAutocomplete id="contractmodal-employee"
                  options={employees.map(e => ({ id: String(e.id), label: `${e.full_name || `${e.first_name} ${e.last_name}`} (${e.rut})` }))}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="Buscar empleado…"
                  required
                />
              )} />
            {errors.employee && <p className={errorCls}>{String(errors.employee.message)}</p>}
          </div>
        )}

        {/* Datos del contrato */}
        <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider">Datos del Contrato</h3>
        <div className="grid grid-cols-2 gap-4">
          <SelectField
            dense
            label="Tipo de contrato"
            required
            selectProps={register('contract_type', { required: 'Obligatorio' })}
          >
            <option value="indefinido">Indefinido</option>
            <option value="plazo_fijo">Plazo Fijo</option>
            <option value="obra">Por Obra o Faena</option>
          </SelectField>
          <div>
            <label htmlFor="contractmodal-base-salary" className={labelCls}>Sueldo Base ($) <span className="text-danger-solid">*</span></label>
            <input id="contractmodal-base-salary" type="number" step="1" {...register('base_salary', { required: 'Obligatorio' })} placeholder="1500000" className={inputCls} />
            {errors.base_salary && <p className={errorCls}>{String(errors.base_salary.message)}</p>}
          </div>
          <SelectField
            dense
            label="Gratificación"
            selectProps={register('gratification_mode')}
          >
            <option value="legal">Legal (25% con tope 4,75 IMM)</option>
            <option value="fixed">Monto fijo pactado</option>
            <option value="none">Sin gratificación</option>
          </SelectField>
          {gratificationMode === 'fixed' && (
            <div>
              <label htmlFor="contractmodal-gratification-fixed-amount" className={labelCls}>Gratificación fija mensual ($) <span className="text-danger-solid">*</span></label>
              <input id="contractmodal-gratification-fixed-amount" type="number" step="1"
                {...register('gratification_fixed_amount', {
                  validate: v => gratificationMode !== 'fixed' || (!!v && Number(v) > 0) || 'Ingrese el monto pactado.',
                })}
                placeholder="170000" className={inputCls} />
              {errors.gratification_fixed_amount && <p className={errorCls}>{String(errors.gratification_fixed_amount.message)}</p>}
              <p className="mt-1 text-xs text-content-subtle">Se prorratea por días trabajados.</p>
            </div>
          )}
          <TextField
            dense
            label="Cargo"
            placeholder="Ej: Gerente Comercial"
            inputProps={register('position')}
          />
          <TextField
            dense
            label="Departamento"
            placeholder="Ej: Ventas"
            inputProps={register('department')}
          />
          <div>
            <label htmlFor="contractmodal-start-date" className={labelCls}>Fecha inicio <span className="text-danger-solid">*</span></label>
            <Controller name="start_date" control={control} rules={{ required: 'Obligatorio' }}
              render={({ field }) => <DatePicker id="contractmodal-start-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} required />} />
            {errors.start_date && <p className={errorCls}>{String(errors.start_date.message)}</p>}
          </div>
          {contractType !== 'indefinido' && (
            <div>
              <label htmlFor="contractmodal-end-date" className={labelCls}>Fecha término</label>
              <Controller name="end_date" control={control}
                render={({ field }) => <DatePicker id="contractmodal-end-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />} />
            </div>
          )}
          <TextField
            dense
            label="Horas semanales"
            inputProps={{ ...register('work_hours_weekly'), type: 'number', step: '0.5' }}
          />
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
              <input type="checkbox" {...register('is_current')} className="rounded border-divider-strong" />
              Contrato vigente
            </label>
          </div>
        </div>

        {/* Documento del contrato */}
        <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider pt-2">Documento del Contrato</h3>
        <div className="grid grid-cols-2 gap-4">
          <TextField
            dense
            label="Enlace externo"
            placeholder="https://drive.google.com/... o PandaDoc, DocuSign"
            inputProps={register('document_url')}
            hint="Google Drive, PandaDoc, DocuSign, etc."
          />
          <div>
            <label htmlFor="contractmodal-document-signed-at" className={labelCls}>Fecha de firma</label>
            <Controller name="document_signed_at" control={control}
              render={({ field }) => <DatePicker id="contractmodal-document-signed-at" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />} />
          </div>
          <TextField
            dense
            className="col-span-2"
            label="Observaciones"
            multiline
            rows={2}
            placeholder="Cláusulas especiales, notas..."
            inputProps={register('notes')}
          />
        </div>
        <p className="text-xs text-content-subtle">Para adjuntar un archivo PDF o Word, use el enlace externo o suba el archivo desde la vista de detalle del contrato.</p>

        <div className="flex justify-end gap-3 pt-4 border-t border-divider">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">Cancelar</button>
          <button type="submit" disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {isPending && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Crear contrato'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
